/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

"use client";

import { CosmosPagination, ResponsiveContainer, SectionContainer } from "@cosmos/components";
import { PageSearchFragment } from "@cosmos/hygraph-schema";

import { css } from "@panda/css";
import { VStack } from "@panda/jsx";

import { CosmosSearchResponse } from "../types";

import { SearchHeader } from "./SearchHeader.element";
import { SearchLoading } from "./SearchLoading.element";
import { SearchNoResults } from "./SearchNoResults.element";
import { SearchResults } from "./SearchResults.element";

import type { ChangeEvent, KeyboardEvent, MouseEvent } from "react";

const searchContentStyles = css({
	maxWidth: "1108px",
	margin: "0 auto",
	position: "relative",
});

export type SearchSectionProps = Omit<PageSearchFragment, "slug" | "locale" | "defaultSearchFilter"> & {
	searchValue?: string;
	currentFilter?: string;
	noFilterPath?: string;
	defaultFilter?: string;
	isLoading?: boolean;
	currentPage?: number;
	searchResults?: CosmosSearchResponse;
	onValueChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
	onFilterChange?: (value: string, event?: KeyboardEvent<HTMLAnchorElement> | MouseEvent<HTMLAnchorElement>) => void;
	onPageChange?: (value: number) => void;
	loadingComponent?: React.ReactNode;
};

export const SearchSection = ({
	searchValue,
	currentPage,
	searchResults,
	noResultsLabel,
	suggestedLinks,
	isLoading,
	loadingLabel,
	loadingComponent,
	onPageChange,
	...props
}: SearchSectionProps): React.JSX.Element => {
	const resultsLoaded = !!searchResults;
	const hasResults = !!searchResults?.items?.length;
	const hasValue = !!searchValue?.length;

	return (
		<ResponsiveContainer>
			<SectionContainer disablePaddingBlockStart={true}>
				<VStack alignItems="stretch" gap="0" className="search-section-section">
					<SearchHeader {...props} searchValue={searchValue} />
					<VStack className={searchContentStyles} gap="eds.dimension.40">
						{(isLoading || !resultsLoaded) && (
							<SearchLoading loadingComponent={loadingComponent} loadingLabel={loadingLabel} />
						)}
						{resultsLoaded && !hasResults && hasValue && (
							<SearchNoResults
								searchValue={searchValue}
								noResultsLabel={noResultsLabel}
								suggestedLinks={suggestedLinks}
							/>
						)}
						{hasResults && hasValue && (
							<>
								<SearchResults currentPage={currentPage} searchResults={searchResults} />
								<CosmosPagination
									current={currentPage}
									pageSize={10}
									total={searchResults?.total}
									handlePageChange={onPageChange}
								/>
							</>
						)}
					</VStack>
				</VStack>
			</SectionContainer>
		</ResponsiveContainer>
	);
};
